import React, { useEffect, useState, useMemo } from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Box,
  Typography,
  Avatar,
  Link as MuiLink,
  TextField,
  Chip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  fetchStoreOrders,
  acceptOrderItem,
  rejectOrderItem,
} from '../../services/store';
import { createShippingLabel } from '../../services/shipping';
import type { ShippingLabelResponseDTO } from '../../dto/shipping/ShippingLabelResponseDTO';
import { showError, showSuccess } from '../Alert';

interface StoreOrderItem {
  id: string;
  itemId: string;
  itemName: string;
  itemPrice: number;
  quantity: number;
  status: string;
  thumbnail: string;
  color: string | null;
  size: string | null;
  trackingNumber?: string | null;
  trackingLink?: string | null;
  carrier?: string | null;
}

interface StoreOrder {
  id: string;
  status: string;
  createdAt: string;
  subtotal: number;
  items: StoreOrderItem[];
  trackingNumber?: string | null;
  trackingLink?: string | null;
  expectedShippingDate?: string | null;
}

type OrderItemRow = {
  order: StoreOrder;
  item: StoreOrderItem;
  label?: ShippingLabelResponseDTO;
};

const toDateInputValue = (date: Date) => date.toISOString().slice(0, 10);

const daysAgo = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toDateInputValue(date);
};

const parseOrderDate = (value?: string) => {
  if (!value) return 0;
  return Date.parse(value.replace(' ', 'T')) || 0;
};

const isInDateRange = (value: string | undefined, from: string, to: string) => {
  const timestamp = parseOrderDate(value);
  if (!timestamp) return false;

  const fromTime = from ? new Date(`${from}T00:00:00`).getTime() : 0;
  const toTime = to ? new Date(`${to}T23:59:59`).getTime() : Infinity;

  return timestamp >= fromTime && timestamp <= toTime;
};

export default function OrdersTab() {
  const [orders, setOrders] = useState<StoreOrder[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => daysAgo(90));
  const [dateTo, setDateTo] = useState(() => toDateInputValue(new Date()));
  const [shippingLabels, setShippingLabels] = useState<
    Record<string, ShippingLabelResponseDTO | undefined>
  >({});
  const [expandedOrders, setExpandedOrders] = useState<Record<string, boolean>>(
    {}
  );

  const formatDate = (value: string | null | undefined) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const formatPrice = (value: number) =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'CAD',
    }).format(value);

  const firstPopulatedValue = (...values: Array<string | null | undefined>) =>
    values.find((value) => Boolean(value && value.trim()));


  const categorizedOrders = useMemo(() => {
    const buckets: Record<'pending' | 'shipped' | 'rejected', StoreOrder[]> = {
      pending: [],
      shipped: [],
      rejected: [],
    };

    orders?.forEach((order) => {
      const label = shippingLabels[order.id];
      const allShipped = order.items.every((item) => item.status === 'SHIPPED');
      const hasLabel =
        Boolean(label) || allShipped || order.status === 'SHIPPED';
      const allRejected = order.items.every(
        (item) => item.status === 'REJECTED_REFUND_IN_PROGRESS'
      );
      const withinDateRange = isInDateRange(order.createdAt, dateFrom, dateTo);

      if (hasLabel) {
        if (withinDateRange) {
          buckets.shipped.push({ ...order, status: 'SHIPPED' });
        }
      } else if (allRejected) {
        if (withinDateRange) {
          buckets.rejected.push(order);
        }
      } else {
        buckets.pending.push(order);
      }
    });

    const byNewest = (a: StoreOrder, b: StoreOrder) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

    buckets.pending.sort(byNewest);
    buckets.shipped.sort(byNewest);
    buckets.rejected.sort(byNewest);

    return buckets;
  }, [dateFrom, dateTo, orders, shippingLabels]);

  const load = () => {
    setLoading(true);
    setShippingLabels({});
    setExpandedOrders({});
    fetchStoreOrders()
      .then((res) =>
        setOrders(
          res.data
            .slice()
            .sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            )
        )
      )
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const updateItemStatus = async (
    orderId: string,
    item: StoreOrderItem,
    newStatus: 'ACCEPTED' | 'REJECTED_REFUND_IN_PROGRESS'
  ) => {
    const call = newStatus === 'ACCEPTED' ? acceptOrderItem : rejectOrderItem;
    try {
      await call(orderId, item.id);
      showSuccess(`Order item marked as ${newStatus}.`);
      setOrders((prev) =>
        prev
          ? prev.map((o) =>
              o.id === orderId
                ? {
                    ...o,
                    items: o.items.map((i) =>
                      i.id === item.id ? { ...i, status: newStatus } : i
                    ),
                  }
                : o
            )
          : prev
      );
    } catch (err: any) {
      showError(
        err?.response?.data?.message || 'Failed to update order item status'
      );
    }
  };

  const createLabelForOrder = async (order: StoreOrder) => {
    if (shippingLabels[order.id]) {
      showSuccess('Shipping label already created for this order.');
      return;
    }

    const hasAccepted = order.items.some((item) => item.status === 'ACCEPTED');

    const allResolved = order.items.every(
      (item) =>
        item.status === 'ACCEPTED' ||
        item.status === 'SHIPPED' ||
        item.status === 'REJECTED_REFUND_IN_PROGRESS'
    );

    if (!hasAccepted) {
      showError('At least one item must be accepted before shipping.');
      return;
    }

    if (!allResolved) {
      showError('Accept or reject each item before shipping the order.');
      return;
    }
    try {
      const { data } = await createShippingLabel({ storeOrderId: order.id });
      if (data?.labelUrl) {
        window.open(data.labelUrl, '_blank', 'noopener');
      }
      setShippingLabels((prev) => ({ ...prev, [order.id]: data }));
      const trackingMsg = data?.trackingNumber
        ? ` Tracking: ${data.trackingNumber}`
        : '';
      showSuccess(`Shipping label created.${trackingMsg}`);
      setOrders((prev) =>
        prev
          ? prev.map((o) => {
              if (o.id !== order.id) return o;
              const updatedItems = o.items.map((i) =>
                i.status === 'ACCEPTED' ? { ...i, status: 'SHIPPED' } : i
              );
              return {
                ...o,
                status: 'SHIPPED',
                items: updatedItems,
              };
            })
          : prev
      );
    } catch (err: any) {
      showError(
        err?.response?.data?.message ||
          'Failed to create shipping label for this order.'
      );
    }
  };

  const renderActions = (
    row: OrderItemRow,
    category: 'pending' | 'shipped' | 'rejected'
  ) => {
    const { order, item, label } = row;
    const isAccepted = item.status === 'ACCEPTED';
    const isShipped = item.status === 'SHIPPED';
    const isRejected = item.status === 'REJECTED_REFUND_IN_PROGRESS';

    if (category === 'pending') {
      return (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Button
            size="small"
            disabled={isAccepted || isShipped}
            onClick={() => updateItemStatus(order.id, item, 'ACCEPTED')}
          >
            Accept
          </Button>
          <Button
            size="small"
            disabled={isRejected || isShipped}
            onClick={() =>
              updateItemStatus(order.id, item, 'REJECTED_REFUND_IN_PROGRESS')
            }
          >
            Reject
          </Button>
        </Stack>
      );
    }

    if (category === 'shipped') {
      const trackingLink = firstPopulatedValue(
        item.trackingLink,
        label?.trackingLink,
        order.trackingLink
      );
      const labelUrl = label?.labelUrl;

      if (trackingLink) {
        return (
          <Button
            size="small"
            onClick={() => window.open(trackingLink, '_blank', 'noopener')}
          >
            Track Shipment
          </Button>
        );
      }

      if (labelUrl) {
        return (
          <Button
            size="small"
            onClick={() => window.open(labelUrl, '_blank', 'noopener')}
          >
            View Shipment
          </Button>
        );
      }

      return (
        <Typography variant="body2" color="textSecondary">
          Shipment details unavailable
        </Typography>
      );
    }

    return (
      <Typography variant="body2" color="textSecondary">
        No actions available
      </Typography>
    );
  };

  const renderSection = (
    title: string,
    emptyMessage: string,
    ordersInSection: StoreOrder[],
    category: 'pending' | 'shipped' | 'rejected'
  ) => (
    <Stack spacing={2} key={category}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Chip
          label={`${ordersInSection.length} orders`}
          size="small"
          variant="outlined"
          sx={{ background: '#fff' }}
        />
      </Stack>
      {ordersInSection.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            background: category === 'pending' ? '#fffaf0' : '#fafafa',
            borderColor: category === 'pending' ? '#eadfcd' : '#eee',
          }}
        >
          <Typography variant="body2" color="textSecondary">
            {emptyMessage}
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderColor: '#eadfcd', boxShadow: '0 16px 44px rgba(31,31,31,0.06)' }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={48} />
                <TableCell align="left">Order ID</TableCell>
                <TableCell align="left">Placed</TableCell>
                <TableCell align="center">Items</TableCell>
                <TableCell align="center">Subtotal</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="left">Shipping</TableCell>
                <TableCell align="center">Order Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ordersInSection.map((order) => {
                const isOpen = expandedOrders[order.id] || false;
                const label = shippingLabels[order.id];
                const hasLabel = Boolean(label);
                const hasAccepted = order.items.some(
                  (item) => item.status === 'ACCEPTED'
                );
                const allAcceptedOrShipped = order.items.every(
                  (item) =>
                    item.status === 'ACCEPTED' ||
                    item.status === 'SHIPPED' ||
                    item.status === 'REJECTED_REFUND_IN_PROGRESS'
                );
                const orderActions = () => {
                  if (category === 'pending') {
                    return (
                      <Button
                        size="small"
                        disabled={
                          !hasAccepted || !allAcceptedOrShipped || hasLabel
                        }
                        onClick={() => createLabelForOrder(order)}
                      >
                        Ship Order
                      </Button>
                    );
                  }
                  if (category === 'shipped') {
                    const trackingLink = firstPopulatedValue(
                      label?.trackingLink,
                      order.trackingLink,
                      ...order.items.map((item) => item.trackingLink)
                    );
                    if (trackingLink) {
                      return (
                        <Button
                          size="small"
                          onClick={() =>
                            window.open(trackingLink, '_blank', 'noopener')
                          }
                        >
                          Track Shipment
                        </Button>
                      );
                    }

                    if (label?.labelUrl) {
                      return (
                        <Button
                          size="small"
                          onClick={() =>
                            window.open(label.labelUrl, '_blank', 'noopener')
                          }
                        >
                          View Shipment
                        </Button>
                      );
                    }

                    return (
                      <Typography variant="body2" color="textSecondary">
                        Shipment details unavailable
                      </Typography>
                    );
                  }
                  return (
                    <Typography variant="body2" color="textSecondary">
                      No actions available
                    </Typography>
                  );
                };

                const shippingSummary = () => {
                  const trackingNumber = firstPopulatedValue(
                    label?.trackingNumber,
                    order.trackingNumber,
                    ...order.items.map((item) => item.trackingNumber)
                  );
                  const trackingLink = firstPopulatedValue(
                    label?.trackingLink,
                    order.trackingLink,
                    ...order.items.map((item) => item.trackingLink)
                  );
                  const eta =
                    label?.expectedDeliveryDate ?? order.expectedShippingDate;

                  if (trackingNumber || trackingLink || label?.labelUrl || eta) {
                    return (
                      <Stack spacing={0.5} alignItems="flex-start">
                        {trackingLink ? (
                          <MuiLink
                            href={trackingLink}
                            target="_blank"
                            rel="noopener"
                          >
                            Track package
                          </MuiLink>
                        ) : null}
                        {label?.labelUrl ? (
                          <MuiLink
                            href={label.labelUrl}
                            target="_blank"
                            rel="noopener"
                          >
                            Download label
                          </MuiLink>
                        ) : null}
                        <Typography variant="body2">
                          Tracking: {trackingNumber || '—'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ETA: {formatDate(eta)}
                        </Typography>
                      </Stack>
                    );
                  }

                  if (category === 'pending') {
                    return (
                      <Typography variant="body2" color="textSecondary">
                        Awaiting shipment
                      </Typography>
                    );
                  }

                  if (category === 'shipped') {
                    return (
                      <Typography variant="body2" color="textSecondary">
                        Shipment details unavailable
                      </Typography>
                    );
                  }

                  return '—';
                };

                return (
                  <React.Fragment key={order.id}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setExpandedOrders((prev) => ({
                              ...prev,
                              [order.id]: !isOpen,
                            }))
                          }
                        >
                          {isOpen ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>{order.id}</TableCell>
                      <TableCell>{formatDate(order.createdAt)}</TableCell>
                      <TableCell align="center">{order.items.length}</TableCell>
                      <TableCell align="center">
                        {formatPrice(order.subtotal)}
                      </TableCell>
                      <TableCell align="center">{order.status}</TableCell>
                      <TableCell>{shippingSummary()}</TableCell>
                      <TableCell align="center">{orderActions()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        sx={{ p: 0, borderBottom: 'none' }}
                      >
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box m={2}>
                            <Typography variant="subtitle1" gutterBottom>
                              Items
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell align="left">Product</TableCell>
                                  <TableCell align="center">Size</TableCell>
                                  <TableCell align="center">Color</TableCell>
                                  <TableCell align="center">Qty</TableCell>
                                  <TableCell align="center">Price</TableCell>
                                  <TableCell align="center">Status</TableCell>
                                  <TableCell align="center">Tracking</TableCell>
                                  <TableCell align="center">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {order.items.map((item) => {
                                  const row: OrderItemRow = {
                                    order,
                                    item,
                                    label,
                                  };
                                  const categoryForRow:
                                    | 'pending'
                                    | 'shipped'
                                    | 'rejected' =
                                    item.status === 'SHIPPED'
                                      ? 'shipped'
                                      : item.status ===
                                        'REJECTED_REFUND_IN_PROGRESS'
                                      ? 'rejected'
                                      : 'pending';

                                  return (
                                    <TableRow key={item.id}>
                                      <TableCell>
                                        <Stack
                                          direction="row"
                                          spacing={1}
                                          alignItems="center"
                                        >
                                          <Avatar
                                            variant="rounded"
                                            src={item.thumbnail}
                                            alt={item.itemName}
                                            sx={{ width: 48, height: 48 }}
                                          />
                                          <MuiLink
                                            component={RouterLink}
                                            to={`/product/${item.itemId}`}
                                            underline="hover"
                                          >
                                            {item.itemName}
                                          </MuiLink>
                                        </Stack>
                                      </TableCell>
                                      <TableCell align="center">
                                        {item.size ?? '—'}
                                      </TableCell>
                                      <TableCell align="center">
                                        {item.color ?? '—'}
                                      </TableCell>
                                      <TableCell align="center">
                                        {item.quantity}
                                      </TableCell>
                                      <TableCell align="center">
                                        {formatPrice(item.itemPrice)}
                                      </TableCell>
                                      <TableCell align="center">
                                        {item.status}
                                      </TableCell>
                                      <TableCell align="center">
                                        {item.trackingLink ? (
                                          <MuiLink
                                            href={item.trackingLink}
                                            target="_blank"
                                            rel="noopener"
                                          >
                                            {item.trackingNumber || 'Track'}
                                          </MuiLink>
                                        ) : (
                                          item.trackingNumber || '—'
                                        )}
                                      </TableCell>
                                      <TableCell align="center">
                                        {renderActions(row, categoryForRow)}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );

  if (loading || !orders) return <CircularProgress />;

  return (
    <Stack spacing={4} sx={{ mt: 2 }}>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          borderColor: '#eadfcd',
          background:
            'linear-gradient(135deg, #fffaf0 0%, #ffffff 56%, #f7efe0 100%)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
        >
          <Box>
            <Typography
              variant="overline"
              sx={{ color: '#8b7651', fontWeight: 800, letterSpacing: '0.16em' }}
            >
              Store operations
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#111' }}>
              Orders
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Date filters apply to shipped and rejected orders. Pending fulfillment stays visible until resolved.
            </Typography>
          </Box>
          <Chip label={`${orders.length} total orders`} variant="outlined" sx={{ background: '#fff' }} />
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ mt: 3 }}
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <TextField
            label="From"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="outlined"
            onClick={() => {
              setDateFrom(daysAgo(90));
              setDateTo(toDateInputValue(new Date()));
            }}
          >
            Last 90 days
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setDateFrom('');
              setDateTo('');
            }}
          >
            Show all
          </Button>
        </Stack>
      </Paper>

      {renderSection(
        'Pending fulfillment',
        'No pending orders at the moment.',
        categorizedOrders.pending,
        'pending'
      )}
      {renderSection(
        'Shipped',
        'No orders have been shipped yet.',
        categorizedOrders.shipped,
        'shipped'
      )}
      {renderSection(
        'Rejected',
        'No rejected orders.',
        categorizedOrders.rejected,
        'rejected'
      )}
    </Stack>
  );
}
